import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { tx } from "@/i18n";

const split = (v) => {
  const [d = "", t = ""] = String(v || "").split("T");
  return [d.slice(0, 10), t.slice(0, 5)];
};

export const BookingTimeDialog = ({ booking, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ date: "", start: "", endDate: "", end: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!booking) return;
    const [d, s] = split(booking.start);
    const [ed, e] = split(booking.end || booking.start);
    setF({ date: d, start: s || "10:00", endDate: ed || d, end: e || s || "11:00" });
  }, [booking]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value, ...(k === "date" && p.endDate === p.date ? { endDate: e.target.value } : {}) }));

  const save = async () => {
    const start = `${f.date}T${f.start}`, end = `${f.endDate || f.date}T${f.end}`;
    if (!f.date || !f.start || !f.end) return toast.error(tx("Vul datum en tijden in"));
    if (end < start) return toast.error(tx("De eindtijd ligt vóór de starttijd"));
    setBusy(true);
    try {
      const b = booking;
      const { data } = await api.put(`/bookings/${b.id}`, {
        house_id: b.house_id, title: b.title, type: b.type, start, end, guest_name: b.guest_name || "",
        assignee_id: b.assignee_id || null, notes: b.notes || "", status: b.status || "scheduled", cash: !!b.cash,
      });
      toast.success(tx("Tijden aangepast"));
      onOpenChange(false); onSaved?.({ ...b, start: data?.start || start, end: data?.end || end });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!booking} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl" data-testid="booking-time-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{tx("Tijden aanpassen")}</DialogTitle>
          <DialogDescription className="truncate">{booking?.title}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>{tx("Datum")}</Label><Input type="date" value={f.date} onChange={set("date")} data-testid="booking-time-date" /></div>
          <div className="space-y-1.5"><Label>{tx("Starttijd")}</Label><Input type="time" value={f.start} onChange={set("start")} data-testid="booking-time-start" /></div>
          <div className="space-y-1.5"><Label>{tx("Einddatum")}</Label><Input type="date" value={f.endDate} onChange={set("endDate")} data-testid="booking-time-end-date" /></div>
          <div className="space-y-1.5"><Label>{tx("Eindtijd")}</Label><Input type="time" value={f.end} onChange={set("end")} data-testid="booking-time-end" /></div>
        </div>
        <p className="text-xs text-muted-foreground">{tx("De wijziging wordt ook in Google Agenda doorgevoerd en de medewerker krijgt een melding.")}</p>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="booking-time-cancel">{tx("Annuleren")}</Button>
          <Button onClick={save} disabled={busy} className="bg-primary hover:bg-primary/90 gap-2" data-testid="booking-time-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {tx("Opslaan")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
